import React, { useEffect, useState } from 'react';
import Navbar from '../components/core/Navbar';
import Footer from '../components/core/Footer';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import './ModelsPage.css';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const ModelsPage = () => {
  const [modelData, setModelData] = useState([]);
  const [filteredModels, setFilteredModels] = useState([]);
  const [selectedModel, setSelectedModel] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('mmc_win_rate');

  // Featured model configuration - easy to change
  const FEATURED_MODEL = '08501_13813_v50_222_111_1f';

  // Fetch data from models_data.json
  useEffect(() => {
    fetch('/assets/models/model_stats_full.json')
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.text();  // Get the raw text first
      })
      .then((text) => {
        try {
          const data = JSON.parse(text);  // Try to parse it
          // Convert object to array
          const modelsArray = Object.entries(data).map(([key, value]) => ({
            model_name: key,
            ...value
          }));
          setModelData(modelsArray);
          setFilteredModels(sortModels(modelsArray, sortBy));
        } catch (e) {
          console.error('Error parsing JSON:', e);
          console.log('Received text:', text.substring(0, 200));  // Log the first 200 chars
          throw e;
        }
      })
      .catch((error) => {
        console.error('Error loading model data:', error);
        console.error('Error details:', error.message);
      });
  }, [sortBy]);

  // Sort models based on selected criteria
  const sortModels = (models, criterion) => {
    return [...models].sort((a, b) => {
      if (criterion === 'mmc_win_rate') return b.mmc_win_rate - a.mmc_win_rate;
      if (criterion === 'corr_win_rate') return b.corr_win_rate - a.corr_win_rate;
      if (criterion === 'active_rounds') return b.active_rounds - a.active_rounds;
      if (criterion === 'avg_mmc') return b.avg_mmc - a.avg_mmc;
      if (criterion === 'avg_corr') return b.avg_corr - a.avg_corr;
      if (criterion === 'projected_avg_mmc') {
        // Handle null values in projected metrics
        if (!a.projected_avg_mmc && !b.projected_avg_mmc) return 0;
        if (!a.projected_avg_mmc) return 1;
        if (!b.projected_avg_mmc) return -1;
        return b.projected_avg_mmc - a.projected_avg_mmc;
      }
      if (criterion === 'projected_avg_corr') {
        if (!a.projected_avg_corr && !b.projected_avg_corr) return 0;
        if (!a.projected_avg_corr) return 1;
        if (!b.projected_avg_corr) return -1;
        return b.projected_avg_corr - a.projected_avg_corr;
      }
      if (criterion === 'projected_win_rate_mmc') {
        if (!a.projected_win_rate_mmc && !b.projected_win_rate_mmc) return 0;
        if (!a.projected_win_rate_mmc) return 1;
        if (!b.projected_win_rate_mmc) return -1;
        return b.projected_win_rate_mmc - a.projected_win_rate_mmc;
      }
      if (criterion === 'projected_win_rate_corr') {
        if (!a.projected_win_rate_corr && !b.projected_win_rate_corr) return 0;
        if (!a.projected_win_rate_corr) return 1;
        if (!b.projected_win_rate_corr) return -1;
        return b.projected_win_rate_corr - a.projected_win_rate_corr;
      }
      return 0;
    });
  };

  // Handle search and filter
  const handleSearch = (e) => {
    const query = e.target.value.toLowerCase();
    setSearchQuery(query);
    
    const filtered = modelData.filter(model =>
      model.model_name.toLowerCase().includes(query)
    );
    setFilteredModels(sortModels(filtered, sortBy));
  };

  // Handle sort change
  const handleSortChange = (criterion) => {
    setSortBy(criterion);
    setFilteredModels(sortModels(filteredModels, criterion));
  };

  // Model card component
  const ModelCard = ({ model }) => {
    const isFeaturedModel = model.model_name === FEATURED_MODEL;

    const chartData = {
      labels: model.projections.map(p => new Date(p.date).toLocaleDateString()).reverse(),
      datasets: [
        {
          label: 'Correlation',
          data: model.projections.map(p => p.corr).reverse(),
          borderColor: '#D4AF37', // Gold
          backgroundColor: 'rgba(212, 175, 55, 0.1)', // Gold with opacity
          tension: 0.1,
          fill: false
        },
        {
          label: 'MMC',
          data: model.projections.map(p => p.mmc).reverse(),
          borderColor: '#00C853', // Signal Green
          backgroundColor: 'rgba(0, 200, 83, 0.1)', // Signal Green with opacity
          tension: 0.1,
          fill: false
        },
      ],
    };

    return (
      <div 
        className={`model-card 
          ${selectedModel?.model_name === model.model_name ? 'selected' : ''} 
          ${isFeaturedModel ? 'featured-model' : ''}
        `}
        onClick={() => setSelectedModel(model)}
      >
        <div className="model-card-header">
          <h3>{model.model_name}</h3>
        </div>
        <div className="model-card-stats">
          <div className="stat">
            <label>Avg CORR</label>
            <span>{model.avg_corr.toFixed(4)}</span>
          </div>
          <div className="stat">
            <label>Avg MMC</label>
            <span>{model.avg_mmc.toFixed(4)}</span>
          </div>
          <div className="stat">
            <label>Win Rate</label>
            <span>{(model.mmc_win_rate * 100).toFixed(0)}%</span>
          </div>
          <div className="stat">
            <label>Resolved Rounds</label>
            <span>{model.active_rounds}</span>
          </div>
        </div>
        <div className="model-card-chart">
          <Line
            data={chartData}
            options={{
              responsive: true,
              maintainAspectRatio: false,
              plugins: {
                legend: {
                  display: false,
                },
              },
              scales: {
                y: {
                  beginAtZero: true,
                  display: true,
                  ticks: {
                    display: false
                  },
                  grid: {
                    color: function(context) {
                      if (context.tick.value === 0) {
                        return 'rgba(0, 0, 0, 0.5)';
                      }
                      return 'rgba(0, 0, 0, 0.1)';
                    },
                    lineWidth: function(context) {
                      if (context.tick.value === 0) {
                        return 2;
                      }
                      return 1;
                    }
                  }
                },
                x: {
                  display: false,
                },
              },
              elements: {
                line: {
                  borderWidth: 2
                },
                point: {
                  radius: 3,
                  hoverRadius: 5
                }
              }
            }}
            height={40}
          />
        </div>
      </div>
    );
  };

  // Modal component for model details
  const ModelDetailsModal = ({ model, onClose }) => {
    const chartData = {
      labels: model.projections.map(p => new Date(p.date).toLocaleDateString()).reverse(),
      datasets: [
        {
          label: 'Correlation',
          data: model.projections.map(p => p.corr).reverse(),
          borderColor: '#D4AF37', // Gold
          backgroundColor: 'rgba(212, 175, 55, 0.1)', // Gold with opacity
          tension: 0.1,
          fill: false
        },
        {
          label: 'MMC',
          data: model.projections.map(p => p.mmc).reverse(),
          borderColor: '#00C853', // Signal Green
          backgroundColor: 'rgba(0, 200, 83, 0.1)', // Signal Green with opacity
          tension: 0.1,
          fill: false
        },
      ],
    };

    return (
      <div className="model-modal" onClick={onClose}>
        <div className="modal-content" onClick={e => e.stopPropagation()}>
          <button className="close-modal" onClick={onClose}>&times;</button>
          
          <div className="modal-header">
            <div className="header-main">
              <h2>{model.model_name}</h2>
              <div className="header-tags">
                <span className="position-tag">{model.latest_position}</span>
                <span className="rounds-tag">{model.projection_count} projections</span>
              </div>
            </div>
            <div className="header-stats">
              <div className="quick-stat">
                <span className="label">Resolved Rounds</span>
                <span className="value">{model.active_rounds}</span>
              </div>
              <div className="quick-stat">
                <span className="label">Volatility</span>
                <span className="value">{model.volatility_corr.toFixed(4)}</span>
              </div>
            </div>
          </div>

          <div className="stats-columns">
            <div className="stats-column">
              <h3>Historical</h3>
              <div className="compact-stats">
                <div className="stat-row">
                  <span className="label">Avg CORR</span>
                  <span className={`value ${model.avg_corr > 0 ? 'trend-up' : 'trend-down'}`}>
                    {model.avg_corr.toFixed(4)}
                  </span>
                </div>
                <div className="stat-row">
                  <span className="label">Avg MMC</span>
                  <span className={`value ${model.avg_mmc > 0 ? 'trend-up' : 'trend-down'}`}>
                    {model.avg_mmc.toFixed(4)}
                  </span>
                </div>
                <div className="stat-row">
                  <span className="label">CORR Win</span>
                  <span className="value">
                    {(model.corr_win_rate * 100).toFixed(1)}%
                  </span>
                </div>
                <div className="stat-row">
                  <span className="label">MMC Win</span>
                  <span className="value">
                    {(model.mmc_win_rate * 100).toFixed(1)}%
                  </span>
                </div>
              </div>
            </div>

            <div className="stats-column">
              <h3>Projected</h3>
              <div className="compact-stats">
                <div className="stat-row">
                  <span className="label">Avg CORR</span>
                  <span className={`value ${model.projected_avg_corr > 0 ? 'trend-up' : 'trend-down'}`}>
                    {model.projected_avg_corr ? model.projected_avg_corr.toFixed(4) : 'N/A'}
                  </span>
                </div>
                <div className="stat-row">
                  <span className="label">Avg MMC</span>
                  <span className={`value ${model.projected_avg_mmc > 0 ? 'trend-up' : 'trend-down'}`}>
                    {model.projected_avg_mmc ? model.projected_avg_mmc.toFixed(4) : 'N/A'}
                  </span>
                </div>
                <div className="stat-row">
                  <span className="label">CORR Win</span>
                  <span className="value">
                    {model.projected_win_rate_corr ? `${(model.projected_win_rate_corr * 100).toFixed(1)}%` : 'N/A'}
                  </span>
                </div>
                <div className="stat-row">
                  <span className="label">MMC Win</span>
                  <span className="value">
                    {model.projected_win_rate_mmc ? `${(model.projected_win_rate_mmc * 100).toFixed(1)}%` : 'N/A'}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="modal-chart">
            <Line
              data={chartData}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: {
                    position: 'top',
                  },
                  title: {
                    display: true,
                    text: 'Projections Over Time',
                  },
                },
                scales: {
                  y: {
                    beginAtZero: true,
                    grid: {
                      color: function(context) {
                        if (context.tick.value === 0) {
                          return 'rgba(0, 0, 0, 0.5)';
                        }
                        return 'rgba(0, 0, 0, 0.1)';
                      },
                      lineWidth: function(context) {
                        if (context.tick.value === 0) {
                          return 2;
                        }
                        return 1;
                      }
                    },
                    ticks: {
                      callback: function(value) {
                        return value.toFixed(2);
                      }
                    }
                  },
                  x: {
                    grid: {
                      color: 'rgba(0, 0, 0, 0.1)',
                    }
                  }
                },
                elements: {
                  line: {
                    borderWidth: 2
                  },
                  point: {
                    radius: 3,
                    hoverRadius: 5
                  }
                }
              }}
              height={250}
            />
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <Navbar />
      <div className="models-page-content">
        <div className="models-header">
          <div className="title-section">
            <h1>Explore Models</h1>
            <p>Compare and analyze model performance metrics</p>
          </div>
          
          <div className="controls-section">
            <div className="search-box">
              <input
                type="text"
                placeholder="Search models..."
                value={searchQuery}
                onChange={handleSearch}
                className="search-input"
              />
            </div>
            
            <div className="view-controls">
              <select 
                value={sortBy} 
                onChange={(e) => handleSortChange(e.target.value)}
                className="sort-select"
              >
                <optgroup label="Historical">
                  <option value="mmc_win_rate">Sort by MMC Win Rate</option>
                  <option value="corr_win_rate">Sort by CORR Win Rate</option>
                  <option value="active_rounds">Sort by Resolved Rounds</option>
                  <option value="avg_mmc">Sort by Average MMC</option>
                  <option value="avg_corr">Sort by Average CORR</option>
                </optgroup>
                <optgroup label="Projected">
                  <option value="projected_avg_mmc">Sort by Projected MMC</option>
                  <option value="projected_avg_corr">Sort by Projected CORR</option>
                  <option value="projected_win_rate_mmc">Sort by Projected MMC Win Rate</option>
                  <option value="projected_win_rate_corr">Sort by Projected CORR Win Rate</option>
                </optgroup>
              </select>
            </div>
          </div>
        </div>

        <div className="models-content">
          <div className="models-grid">
            {filteredModels.map(model => (
              <ModelCard key={model.model_name} model={model} />
            ))}
          </div>
        </div>
      </div>
      {selectedModel && (
        <ModelDetailsModal 
          model={selectedModel} 
          onClose={() => setSelectedModel(null)} 
        />
      )}
      <Footer />
    </>
  );
};

export default ModelsPage;
